import { useState, useEffect } from 'react';
import { profilesService, CreateProfileRequest } from '@/services/profiles.service';
import { auth } from '@/lib/firebase';
import { useOnboarding } from '@/contexts/OnboardingContext';

interface OnboardingWizardProps {
  onComplete: () => void;
}

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const { setNeedsOnboarding, setProfile } = useOnboarding();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [timezone, setTimezone] = useState('');

  // Pre-fill email from Firebase Auth
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setEmail(user.email || '');
      setName(user.displayName || '');
    }

    // Auto-detect timezone
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(detectedTimezone);
  }, []);

  const handleSubmit = async () => {
    // Validate
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: CreateProfileRequest = {
        name: name.trim(),
        email: email.trim(),
        companyName: companyName.trim() || undefined,
        timezone,
      };

      const profile = await profilesService.createProfile(data);

      // Update context
      setProfile(profile);
      setNeedsOnboarding(false);

      // Immediately trigger navigation - this will unmount the modal
      onComplete();
    } catch (err: any) {
      console.error('Error creating profile:', err);
      setError(err.message || 'Failed to create profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative">
        {/* Welcome Screen */}
        {!showForm && (
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-flowky-primary to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-3">Welcome to Flowky</h1>
            <p className="text-gray-600 mb-8">
              Your all-in-one platform for managing freelance work
            </p>

            <button
              onClick={() => setShowForm(true)}
              className="w-full px-6 py-3.5 bg-flowky-primary text-white rounded-xl hover:bg-flowky-primary/90 transition-all font-semibold shadow-lg hover:shadow-xl"
            >
              Get Started
            </button>
          </div>
        )}

        {/* Profile Setup Form */}
        {showForm && (
          <div>
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Your Profile</h2>
              <p className="text-gray-600">Just a few details to get you started</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-flowky-primary transition-colors"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-600 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1.5">From your account</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Name <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Inc."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-flowky-primary transition-colors"
                />
                <p className="text-xs text-gray-500 mt-1.5">Appears on your invoices</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Timezone</label>
                <input
                  type="text"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-flowky-primary transition-colors"
                />
                <p className="text-xs text-gray-500 mt-1.5">Auto-detected</p>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full mt-6 px-6 py-3.5 bg-flowky-primary text-white rounded-xl hover:bg-flowky-primary/90 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating Profile...
                </>
              ) : (
                'Complete Setup'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
